import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CSATReports from '../../api/csatReports';
import { downloadCsvFile } from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';

const computeDistribution = (value, total) =>
  ((value * 100) / total).toFixed(2);

export const state = {
  records: [],
  questions: [],
  metrics: {
    totalResponseCount: 0,
    ratingsCount: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    },
    totalSentMessagesCount: 0,
    // Nota na régua das avaliações (Csat::ScoreSummary). Escalas misturadas
    // no período = sem número (mixedScales).
    score: null,
    // Uma entrada por escala do período: nota + contagem de cada valor
    // (Csat::ScoreSummary.by_scale). É de onde sai a barra de distribuição.
    scales: [],
    // Pesquisas enviadas contadas na hora (lento) e por quê — banner da tela.
    sentCoverage: null,
  },
  uiFlags: {
    isFetching: false,
    isFetchingMetrics: false,
    isFetchingQuestions: false,
  },
};

export const getters = {
  getCSATResponses(_state) {
    return _state.records;
  },
  getMetrics(_state) {
    return _state.metrics;
  },
  getQuestions(_state) {
    return _state.questions;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getSatisfactionScore(_state) {
    if (!_state.metrics.totalResponseCount) {
      return 0;
    }
    return computeDistribution(
      _state.metrics.ratingsCount[4] + _state.metrics.ratingsCount[5],
      _state.metrics.totalResponseCount
    );
  },
  getResponseRate(_state) {
    if (!_state.metrics.totalSentMessagesCount) {
      return 0;
    }
    return computeDistribution(
      _state.metrics.totalResponseCount,
      _state.metrics.totalSentMessagesCount
    );
  },
};

export const actions = {
  get: async function getResponses({ commit }, params) {
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetching: true });
    try {
      const response = await CSATReports.get(params);
      commit(types.SET_CSAT_RESPONSE, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetching: false });
    }
  },
  getMetrics: async function getMetrics({ commit }, params) {
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingMetrics: true });
    try {
      const response = await CSATReports.getMetrics(params);
      commit(types.SET_CSAT_RESPONSE_METRICS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingMetrics: false });
    }
  },
  getQuestionMetrics: async function getQuestionMetrics({ commit }, params) {
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingQuestions: true });
    try {
      const response = await CSATReports.getQuestionMetrics(params);
      commit(types.SET_CSAT_QUESTION_METRICS, response.data.questions);
    } catch (error) {
      // Pesquisa por pergunta é adicional ao relatório: falhar aqui não pode
      // derrubar os cards nem a tabela.
      commit(types.SET_CSAT_QUESTION_METRICS, []);
    } finally {
      commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingQuestions: false });
    }
  },
  downloadCSATReports(_, params) {
    return CSATReports.download(params).then(response => {
      downloadCsvFile(params.fileName, response.data);
      AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
        reportType: 'csat',
      });
    });
  },
};

export const mutations = {
  [types.SET_CSAT_RESPONSE_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CSAT_RESPONSE]: MutationHelpers.set,
  [types.SET_CSAT_RESPONSE_METRICS](
    _state,
    {
      total_count: totalResponseCount,
      ratings_count: ratingsCount,
      total_sent_messages_count: totalSentMessagesCount,
      score,
      scales,
      sent_coverage: sentCoverage,
    }
  ) {
    _state.metrics.totalResponseCount = totalResponseCount || 0;
    _state.metrics.ratingsCount = {
      1: ratingsCount['1'] || 0,
      2: ratingsCount['2'] || 0,
      3: ratingsCount['3'] || 0,
      4: ratingsCount['4'] || 0,
      5: ratingsCount['5'] || 0,
    };
    _state.metrics.totalSentMessagesCount = totalSentMessagesCount || 0;
    _state.metrics.scales = scales || [];
    _state.metrics.sentCoverage = sentCoverage || null;
    _state.metrics.score = score
      ? {
          value: score.score,
          kind: score.score_kind,
          scaleLabel: score.scale_label,
          scaleMin: score.scale_min,
          scaleMax: score.scale_max,
          mixedScales: score.mixed_scales,
        }
      : null;
  },
  [types.SET_CSAT_QUESTION_METRICS](_state, questions) {
    _state.questions = questions || [];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
